// AdminDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  IconButton,
  Collapse,
  TablePagination,
  Paper,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  Download,
  Cancel,
  MonetizationOn,
  VpnKey,
  RestartAlt,
  PriceChange,
} from "@mui/icons-material";
import { API_BASE } from "../utils/api";
import { listAdminBookings } from "../api/admin/admin";
import { useNavigate } from "react-router-dom";

function useDebouncedValue(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function StatusChip({ status }) {
  const label = status || "active";
  const color =
    label === "refunded" ? "error" : label === "canceled" ? "warning" : "success";

  return (
    <Chip
      label={label}
      color={color}
      variant="outlined"
      size="small"
      sx={{ textTransform: "capitalize", fontWeight: 600 }}
    />
  );
}

function Row({ booking, onAction, onIssueAccess, onLateFee }) {
  const [open, setOpen] = useState(false);

  const timePretty = useMemo(() => {
    const parts = String(booking.time || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const to12h = (t) => {
      const [h, m] = t.split(":").map(Number);
      const d = new Date();
      d.setHours(h || 0);
      d.setMinutes(m || 0);
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    };

    return parts.sort((a, b) => a.localeCompare(b)).map(to12h).join(", ");
  }, [booking.time]);

  const attendeeCount = Math.max(1, Number(booking.attendees) || 1);
  const disabled = booking.status === "canceled" || booking.status === "refunded";

  const pinChip = booking.doorPin ? (
    <Chip
      size="small"
      color="success"
      label={`PIN: ${booking.doorPin}`}
      sx={{ fontFamily: "monospace" }}
    />
  ) : (
    <Chip size="small" color="default" label="PIN: none" />
  );

  const expiresChip = booking.doorPinExpiresAt ? (
    <Chip
      size="small"
      label={`Expires: ${new Date(booking.doorPinExpiresAt).toLocaleString()}`}
    />
  ) : (
    <Chip size="small" label="Expires: —" />
  );

  const sentChip = booking.accessEmailSentAt ? (
    <Chip
      size="small"
      label={`Access sent: ${new Date(booking.accessEmailSentAt).toLocaleString()}`}
    />
  ) : (
    <Chip size="small" label="Access sent: —" />
  );

  const lateFeeChip = booking.lateFeePaymentIntentId ? (
    <Chip
      size="small"
      color="warning"
      label={`Late fee: $${((booking.lateFeeAmount || 0) / 100).toFixed(2)}`}
    />
  ) : null;

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox" sx={{ width: 40 }}>
          <IconButton
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-label="toggle details"
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap", width: 120 }}>
          {booking.date}
        </TableCell>

        <TableCell sx={{ minWidth: 140 }}>{timePretty}</TableCell>

        <TableCell sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
          {booking.firstName} {booking.lastName}
        </TableCell>

        <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
          {booking.phone}
        </TableCell>

        <TableCell sx={{ minWidth: 220 }}>{booking.email}</TableCell>

        <TableCell sx={{ width: 110 }}>
          <StatusChip status={booking.status} />
        </TableCell>

        <TableCell align="right" sx={{ width: 90 }}>
          {attendeeCount}
        </TableCell>

        <TableCell align="right" sx={{ width: 460 }}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            useFlexGap
            flexWrap="wrap"
            sx={{ "& .MuiButton-root": { minWidth: 110 } }}
          >
            <Tooltip
              title={disabled ? "Already canceled/refunded" : "Cancel booking (no refund)"}
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<Cancel />}
                  onClick={() => onAction("cancel", booking)}
                  disabled={disabled}
                >
                  Cancel
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                booking.status === "refunded"
                  ? "Already refunded"
                  : "Refund full or partial amount, and mark booking canceled"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<MonetizationOn />}
                  onClick={() => onAction("refund", booking)}
                  disabled={booking.status === "refunded"}
                >
                  Refund
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={disabled ? "Unavailable for canceled/refunded" : "Email lockbox code + instructions"}
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<VpnKey />}
                  onClick={() => onIssueAccess(booking, false)}
                  disabled={disabled}
                >
                  Issue Access
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={disabled ? "Unavailable for canceled/refunded" : "Regenerate a new PIN and re-send"}
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RestartAlt />}
                  onClick={() => onIssueAccess(booking, true)}
                  disabled={disabled}
                >
                  Regenerate
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                booking.lateFeePaymentIntentId
                  ? "Late fee already charged"
                  : disabled
                  ? "Unavailable for canceled/refunded"
                  : "Charge $50 late fee off-session"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<PriceChange />}
                  onClick={() => onLateFee(booking)}
                  disabled={disabled || !!booking.lateFeePaymentIntentId}
                >
                  Late Fee
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={9}
          sx={{ py: 0, backgroundColor: open ? "rgba(0,0,0,0.02)" : "inherit" }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Booking Details
              </Typography>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={`Booking ID: ${booking.id}`}
                  sx={{ fontFamily: "monospace" }}
                />
                <Chip size="small" label={`Duration: ${booking.duration} min`} />
                <Chip size="small" label={`Attendees: ${attendeeCount}`} />
                {pinChip}
                {expiresChip}
                {sentChip}
                {lateFeeChip}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // filters
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const debouncedQ = useDebouncedValue(q, 400);

  // pagination (server-side)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const token = localStorage.getItem("adm_t");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
    }
  }, [token, navigate]);

  async function fetchData() {
    setLoading(true);
    setErr("");

    const params = {
      page: String(page + 1),
      pageSize: String(pageSize),
    };

    if (start) params.start = start;
    if (end) params.end = end;
    if (debouncedQ) params.q = debouncedQ;

    try {
      const { rows, count } = await listAdminBookings(params);
      setRows(rows);
      setCount(count);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!token) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, start, end, debouncedQ, page, pageSize]);

  const onRefresh = () => fetchData();

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => (r.status || "active") === statusFilter);
  }, [rows, statusFilter]);

  async function exportCsv() {
    try {
      const params = new URLSearchParams({
        page: "1",
        pageSize: "10000",
      });

      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (debouncedQ) params.set("q", debouncedQ);

      const res = await fetch(`${API_BASE}/api/admin/bookings?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to load data for CSV");

      let allRows = Array.isArray(data.rows) ? data.rows : [];
      if (statusFilter !== "all") {
        allRows = allRows.filter((r) => (r.status || "active") === statusFilter);
      }

      const headers = [
        "booking_id",
        "date",
        "time",
        "duration",
        "first_name",
        "last_name",
        "phone",
        "email",
        "status",
        "attendees",
        "door_pin",
        "door_pin_expires_at",
        "access_email_sent_at",
        "access_email_to",
        "late_fee_amount",
        "late_fee_payment_intent_id",
        "late_fee_charged_at",
      ];

      const lines = [headers.join(",")];

      for (const b of allRows) {
        const attendeeCount = Math.max(1, Number(b.attendees) || 1);

        const csvRow = [
          b.id,
          b.date,
          (b.time || "").replace(/,/g, ";"),
          b.duration,
          (b.firstName || "").replace(/,/g, " "),
          (b.lastName || "").replace(/,/g, " "),
          (b.phone || "").replace(/,/g, " "),
          b.email || "",
          b.status || "active",
          attendeeCount,
          b.doorPin || "",
          b.doorPinExpiresAt || "",
          b.accessEmailSentAt || "",
          b.accessEmailTo || "",
          Number.isInteger(b.lateFeeAmount) ? b.lateFeeAmount : "",
          b.lateFeePaymentIntentId || "",
          b.lateFeeChargedAt || "",
        ]
          .map((v) => {
            const str = String(v ?? "");
            if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
            return str;
          })
          .join(",");

        lines.push(csvRow);
      }

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookings_${statusFilter}_${start || "all"}_${end || "all"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed:", e);
      alert(e.message || "CSV export failed");
    }
  }

  async function onAction(type, booking) {
    const pretty = `${booking.date} ${booking.time} — ${booking.firstName} ${booking.lastName} <${booking.email}>`;

    if (type === "cancel") {
      const ok = window.confirm(
        `Cancel this booking?\n\n${pretty}\n\n(No refund will be issued.)`
      );
      if (!ok) return;
    } else if (type === "refund") {
      const amountUsd = window.prompt(
        `Refund how much? Leave blank for FULL refund.\n\n${pretty}\n\nEnter amount in USD (e.g., 10 or 10.00)`,
        ""
      );
      if (amountUsd === null) return;

      const body = { reason: "requested_by_customer" };
      const trimmed = amountUsd.trim();

      if (trimmed !== "") {
        const cents = Math.round(parseFloat(trimmed) * 100);
        if (!Number.isFinite(cents) || cents <= 0) {
          alert("Invalid amount.");
          return;
        }
        body.amount = cents;
      }

      try {
        const res = await fetch(`${API_BASE}/api/admin/bookings/${booking.id}/refund`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Failed to ${type} booking`);
        onRefresh();
      } catch (e) {
        console.error(`${type} failed:`, e);
        alert(e.message || `Failed to ${type} booking`);
      }
      return;
    } else {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/bookings/${booking.id}/${type}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: "admin_action" }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed to ${type} booking`);
      onRefresh();
    } catch (e) {
      console.error(`${type} failed:`, e);
      alert(e.message || `Failed to ${type} booking`);
    }
  }

  async function onIssueAccess(booking, forceRegenerate) {
    const confirmText = forceRegenerate
      ? `Generate a NEW 6-digit PIN and email access details to ${booking.email}?`
      : `Send access email with ${
          booking.doorPin ? "existing PIN" : "a new 6-digit PIN"
        } to ${booking.email}?`;

    const ok = window.confirm(confirmText);
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/api/admin/bookings/${booking.id}/issue-access`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ forceRegenerate }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Failed to issue access email");

      alert(
        `Access email sent to ${data.sentTo}\nPIN: ${data.pin}\nExpires: ${
          data.expiresAt ? new Date(data.expiresAt).toLocaleString() : "—"
        }`
      );
      onRefresh();
    } catch (e) {
      console.error("issue-access failed:", e);
      alert(e.message || "Failed to issue access email");
    }
  }

  async function onLateFee(booking) {
    const def = 50;
    const input = window.prompt(
      `Charge late fee amount in USD (default $${def.toFixed(0)}):`,
      String(def)
    );
    if (input === null) return;

    const dollars = Number(input);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    const amountCents = Math.round(dollars * 100);

    try {
      const res = await fetch(`${API_BASE}/api/admin/bookings/${booking.id}/late-fee`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amountCents }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Late fee charge failed");

      alert(
        `Late fee charged: $${(amountCents / 100).toFixed(2)}\nPI: ${
          data?.paymentIntent?.id || "—"
        }`
      );
      onRefresh();
    } catch (e) {
      console.error("late-fee failed:", e);
      alert(e.message || "Late fee charge failed");
    }
  }

  return (
    <Box p={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4">Admin Dashboard</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Download />} variant="outlined" onClick={exportCsv}>
            Export CSV
          </Button>
          <Button startIcon={<Refresh />} variant="outlined" onClick={onRefresh}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            type="date"
            label="Start"
            InputLabelProps={{ shrink: true }}
            value={start}
            onChange={(e) => {
              setPage(0);
              setStart(e.target.value);
            }}
            sx={{ width: { xs: "100%", md: 190 } }}
          />

          <TextField
            type="date"
            label="End"
            InputLabelProps={{ shrink: true }}
            value={end}
            onChange={(e) => {
              setPage(0);
              setEnd(e.target.value);
            }}
            sx={{ width: { xs: "100%", md: 190 } }}
          />

          <TextField
            label="Search (email)"
            placeholder="e.g. user@domain.com"
            value={q}
            onChange={(e) => {
              setPage(0);
              setQ(e.target.value);
            }}
            fullWidth
          />
        </Stack>

        <Tabs
          value={statusFilter}
          onChange={(_, v) => {
            setStatusFilter(v);
            setPage(0);
          }}
          sx={{ mt: 2 }}
        >
          <Tab label="All" value="all" />
          <Tab label="Active" value="active" />
          <Tab label="Canceled" value="canceled" />
          <Tab label="Refunded" value="refunded" />
        </Tabs>
      </Paper>

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Attendees</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : err ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="error">{err}</Typography>
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary">No bookings found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((b) => (
                <Row
                  key={b.id}
                  booking={b}
                  onAction={onAction}
                  onIssueAccess={onIssueAccess}
                  onLateFee={onLateFee}
                />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}